import * as Sentry from '@sentry/react';
import { useAuthStore } from '@/stores/authStore';
import type { AuthTokens, ErrorEnvelope } from '@/lib/auth-types';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Default per-request timeout (PWA #13, 2026-04-28). Wired via
 * `AbortSignal.timeout()` into the fetch `signal` field. On timeout the
 * browser throws a `TimeoutError` (DOMException), caught by the existing
 * try/catch and mapped to `NetworkError` — no separate error class needed.
 *
 * 30s covers the slowest expected request (story-job creation triggers
 * synchronous moderation + entitlement-check calls before queueing); story
 * generation itself is async via SSE and not subject to this timeout.
 */
export const DEFAULT_FETCH_TIMEOUT_MS = 30_000;

export class ApiError extends Error {
  public readonly httpStatus: number;
  public readonly envelope: ErrorEnvelope | null;
  public readonly endpoint: string;

  constructor(httpStatus: number, envelope: ErrorEnvelope | null, endpoint: string) {
    super(envelope?.user_message ?? `HTTP ${httpStatus}`);
    this.name = 'ApiError';
    this.httpStatus = httpStatus;
    this.envelope = envelope;
    this.endpoint = endpoint;
  }

  get errorCode(): string | null {
    return this.envelope?.error_code ?? null;
  }
}

export class NetworkError extends Error {
  public readonly endpoint: string;

  constructor(endpoint: string, options?: { cause?: unknown }) {
    super('Network error');
    this.name = 'NetworkError';
    this.endpoint = endpoint;
    if (options?.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const { refreshToken, clearAuth } = useAuthStore.getState();
      if (!refreshToken) {
        clearAuth();
        return null;
      }

      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        clearAuth();
        return null;
      }

      const tokens = (await res.json()) as AuthTokens;
      useAuthStore.getState().setTokens(tokens.accessToken, tokens.refreshToken);
      return tokens.accessToken;
    } catch {
      useAuthStore.getState().clearAuth();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { skipAuth, body, headers: rawHeaders, ...init } = options;
  const url = `${API_BASE}${path}`;

  const makeRequest = async (accessToken: string | null): Promise<Response> => {
    const headers = new Headers(rawHeaders);
    if (body !== undefined && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (accessToken && !skipAuth) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    const requestInit: RequestInit = {
      ...init,
      headers,
      signal: AbortSignal.timeout(DEFAULT_FETCH_TIMEOUT_MS),
    };
    if (body !== undefined) {
      requestInit.body = JSON.stringify(body);
    }
    return fetch(url, requestInit);
  };

  let res: Response;
  // Tracks whether refreshAccessToken minted a fresh token AND we
  // retried the request with it. Only in that case is a trailing 401
  // surprising enough to warrant Sentry capture (see the gate below).
  let refreshSucceeded = false;
  try {
    const token = skipAuth ? null : useAuthStore.getState().accessToken;
    res = await makeRequest(token);

    if (res.status === 401 && !skipAuth && useAuthStore.getState().refreshToken) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        refreshSucceeded = true;
        res = await makeRequest(newToken);
      }
    }
  } catch (err) {
    // Cross #10 Cluster Y1 — capture network/timeout failures to Sentry.
    // ApiError is captured below (status-gated). Path-only tag preserves
    // the failed endpoint without leaking query params.
    const networkError = new NetworkError(path, { cause: err });
    Sentry.captureException(networkError, { tags: { endpoint: path.split('?')[0] } });
    throw networkError;
  }

  if (!res.ok) {
    let envelope: ErrorEnvelope | null = null;
    try {
      envelope = (await res.json()) as ErrorEnvelope;
    } catch {
      envelope = null;
    }
    const apiError = new ApiError(res.status, envelope, path);
    // Bug PWA-A Bug-A (2026-05-21): capture EVERY non-2xx, unconditionally.
    // The previous gate (status >= 500 || envelope === null) silently dropped
    // all 4xx-with-envelope responses from Sentry — which is exactly the class
    // that hid the reactivate false-error (a 400 subscription_not_pending_cancel
    // with a valid DreamableError envelope produced zero Sentry events). Higher
    // 4xx volume (e.g. routine validation_error form errors) is accepted as the
    // cost of not silently dropping coded client errors; distinct error_codes
    // keep them filterable in Sentry.
    //
    // 2026-11-03 — expected-401 exemption (Sentry issue
    // cda96bf13aa646d392df6c9e2d7e0f25). The known-unauth paths —
    // anonymous (skipAuth) request, no stored session, refreshToken
    // expired at the refresh endpoint — are not bugs; they are a
    // customer whose session is simply not there yet. The ApiError
    // still throws so the caller can route to /auth/login. We only
    // Sentry the surprise case: refreshAccessToken minted a fresh
    // token, the request was retried with it, and the retry STILL
    // returned 401. The non-401 4xx-with-envelope capture (the
    // regression guard from 2026-05-21) stays intact.
    const isExpectedUnauth = res.status === 401 && !refreshSucceeded;
    if (!isExpectedUnauth) {
      Sentry.captureException(apiError, { tags: { endpoint: path.split('?')[0] } });
    }
    throw apiError;
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}
